import React, { useState, useEffect } from 'react';
import { Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import SideModal from '../../components/Common/SideModal';
import { waybillsAPI } from '../../helpers/api';

interface Waybill {
  id: string;
  waybillNumber: string;
  status: string;
  supplierName: string | null;
  notes: string | null;
  createdAt: string;
  scannedAt: string | null;
  site: {
    id: string;
    name: string;
    project: { name: string };
  } | null;
  scannedBy: { firstName: string; lastName: string } | null;
}

interface Site {
  id: string;
  name: string;
  code: string;
  project: { name: string; city: { name: string } };
}

interface SiteWaybillsModalProps {
  isOpen: boolean;
  toggle: () => void;
  site: Site | null;
  onUpdate?: () => void;
}

const statusStyles: Record<string, string> = {
  SCANNED: 'bg-info bg-opacity-10 text-info',
  PENDING: 'bg-warning bg-opacity-10 text-warning',
  MATCHED: 'bg-success bg-opacity-10 text-success',
  NOT_ARRIVED: 'bg-danger bg-opacity-10 text-danger',
  MANUAL: 'bg-secondary bg-opacity-10 text-secondary',
};

const statusLabels: Record<string, string> = {
  SCANNED: 'Okutuldu',
  PENDING: 'Bekliyor',
  MATCHED: 'Eşleşti',
  NOT_ARRIVED: 'Gelmedi',
  MANUAL: 'Manuel',
};

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const SiteWaybillsModal: React.FC<SiteWaybillsModalProps> = ({
  isOpen,
  toggle,
  site,
  onUpdate,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [waybills, setWaybills] = useState<Waybill[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && site) {
      fetchData();
    }
  }, [isOpen, site]);

  const fetchData = async () => {
    if (!site) return;

    try {
      setLoading(true);
      const response = await waybillsAPI.getAll({ siteId: site.id });
      setWaybills(response.data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('İrsaliyeler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = () => setIsExpanded(!isExpanded);

  if (!site) return null;

  const headerActions = (
    <button
      type="button"
      className="side-modal-expand-btn"
      onClick={toggleExpand}
      title={isExpanded ? 'Küçült' : 'Genişlet'}
    >
      <i className={isExpanded ? 'ri-contract-left-right-line' : 'ri-expand-left-right-line'}></i>
    </button>
  );

  // Count by status
  const statusCounts = waybills.reduce((acc, w) => {
    acc[w.status] = (acc[w.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <SideModal
      isOpen={isOpen}
      toggle={toggle}
      title="İrsaliyeler"
      size={isExpanded ? 'expanded' : 'lg'}
      headerActions={headerActions}
      hideBackdrop
    >
      <div style={{ margin: '-1.25rem', padding: '1rem', background: '#f3f3f9', minHeight: '100%' }}>
        {/* Header Card */}
        <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #878a99' }} className="rounded-3 p-3 mb-3">
          <h4 className="mb-1" style={{ fontWeight: 600 }}>
            {site.name}
          </h4>
          <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
            {site.project.city.name} • {site.project.name}
          </p>
        </div>

        {loading ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
          </div>
        ) : (
          <>
            {/* Status Summary */}
            {waybills.length > 0 && (
              <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f7b84b' }} className="rounded-3 p-3 mb-3">
                <div className="text-muted small mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Özet
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {Object.entries(statusCounts).map(([status, count]) => (
                    <div
                      key={status}
                      className={`px-3 py-2 rounded-3 ${statusStyles[status]}`}
                      style={{ fontSize: '0.85rem' }}
                    >
                      <span style={{ fontWeight: 500 }}>{count}</span>
                      <span className="ms-1">{statusLabels[status]}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Waybills List */}
            <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #299cdb' }} className="rounded-3 p-3 mb-3">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <span className="text-muted small" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  İrsaliye Listesi
                </span>
                <span className="text-muted small">{waybills.length} irsaliye</span>
              </div>
              {waybills.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted mb-0">Bu şantiyede henüz irsaliye bulunmuyor</p>
                </div>
              ) : (
                <div style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }} className="rounded-3 overflow-hidden">
                  {waybills.slice(0, 20).map((waybill, idx) => (
                    <div
                      key={waybill.id}
                      className="px-3 py-3"
                      style={{ borderBottom: idx < Math.min(waybills.length, 20) - 1 ? '1px solid #e9ecef' : 'none' }}
                    >
                      <div className="d-flex align-items-start justify-content-between">
                        <div style={{ flex: 1 }}>
                          <div className="d-flex align-items-center mb-1">
                            <span style={{ fontWeight: 500 }}>{waybill.waybillNumber}</span>
                          </div>
                          <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                            {waybill.supplierName && (
                              <span className="me-3">
                                <i className="ri-building-2-line me-1"></i>
                                {waybill.supplierName}
                              </span>
                            )}
                            <span>
                              <i className="ri-time-line me-1"></i>
                              {formatDate(waybill.scannedAt || waybill.createdAt)}
                            </span>
                          </div>
                          {waybill.scannedBy && (
                            <div className="text-muted mt-1" style={{ fontSize: '0.8rem' }}>
                              <i className="ri-user-line me-1"></i>
                              {waybill.scannedBy.firstName} {waybill.scannedBy.lastName}
                            </div>
                          )}
                        </div>
                        <span
                          className={`px-2 py-1 rounded-pill small ${statusStyles[waybill.status]}`}
                          style={{ fontWeight: 500, fontSize: '0.75rem' }}
                        >
                          {statusLabels[waybill.status]}
                        </span>
                      </div>
                    </div>
                  ))}
                  {waybills.length > 20 && (
                    <div className="px-3 py-2 text-center text-muted" style={{ fontSize: '0.85rem' }}>
                      +{waybills.length - 20} irsaliye daha
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </SideModal>
  );
};

export default SiteWaybillsModal;
